(function () {
  const initializedAttribute = 'data-custom-size-pricing-initialized';
  const cropImagePropertyName = 'properties[Crop Image]';
  const cropPreferencePropertyName = 'properties[Crop Preference]';
  const customerUploadSource = 'customer-upload';
  const maxCustomerUploadImages = 8;
  const customerUploadTypes = ['image/jpeg', 'image/png', 'image/webp'];
  const customSizePropertyNames = [
    'properties[_custom_size]',
    'properties[_custom_size_rule]',
    'properties[_custom_unit]',
    'properties[Custom Width]',
    'properties[Custom Height]',
    'properties[Custom Unit]',
    'properties[Custom Material]',
    'properties[Custom Area (m2)]',
    cropPreferencePropertyName,
    'properties[_frontend_calculated_price]',
  ];
  const cropExportType = 'image/png';
  const maxCropExportBytes = 20 * 1024 * 1024;

  const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const roundTo = (value, places) => {
    const factor = Math.pow(10, places);
    return Math.round((value + Number.EPSILON) * factor) / factor;
  };

  const ceilTo = (value, places) => {
    const factor = Math.pow(10, places);
    return Math.ceil((value - Number.EPSILON) * factor) / factor;
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getDistance = (pointA, pointB) => Math.hypot(pointA.x - pointB.x, pointA.y - pointB.y);

  const getMidpoint = (pointA, pointB) => ({
    x: (pointA.x + pointB.x) / 2,
    y: (pointA.y + pointB.y) / 2,
  });

  const hasMaxTwoDecimals = (value) => /^\d+(\.\d{1,2})?$/.test(String(value).trim());
  const getCustomerUploadPropertyName = (index) => `properties[Image${index + 1}]`;

  const parseVariantDimension = (value) => {
    const text = String(value || '')
      .trim()
      .replace(/[’‘′]/g, "'");
    if (!text) return null;

    const normalizeUnit = (unit) => {
      const normalized = String(unit || '').trim().toLowerCase();
      if (normalized === 'ft' || normalized === 'pi' || normalized === "'") return 'ft';
      if (normalized === 'm') return 'm';
      return '';
    };
    const normalizeLabel = (label) => {
      const normalized = String(label || '').trim().toLowerCase();
      if (normalized === 'w' || normalized === 'width') return 'width';
      if (normalized === 'h' || normalized === 'height') return 'height';
      return '';
    };
    const dimensionPattern =
      /(\d+(?:\.\d{1,2})?)\s*(ft|m|pi|')?\s*(w(?:idth)?|h(?:eight)?)?\s*[x×*]\s*(\d+(?:\.\d{1,2})?)\s*(ft|m|pi|')?\s*(w(?:idth)?|h(?:eight)?)?(?=$|\s|[-(/),])/gi;

    let match;
    while ((match = dimensionPattern.exec(text))) {
      const leftUnit = normalizeUnit(match[2]);
      const rightUnit = normalizeUnit(match[5]);
      if (!leftUnit && !rightUnit) continue;
      if (leftUnit && rightUnit && leftUnit !== rightUnit) return null;

      const leftLabel = normalizeLabel(match[3]);
      const rightLabel = normalizeLabel(match[6]);
      if (leftLabel && rightLabel && leftLabel === rightLabel) return null;

      let widthRaw = match[1];
      let heightRaw = match[4];
      if (leftLabel === 'height' || rightLabel === 'width') {
        widthRaw = match[4];
        heightRaw = match[1];
      }

      const width = toNumber(widthRaw);
      const height = toNumber(heightRaw);
      if (
        width === null ||
        height === null ||
        width <= 0 ||
        height <= 0 ||
        !hasMaxTwoDecimals(widthRaw) ||
        !hasMaxTwoDecimals(heightRaw)
      ) {
        return null;
      }

      return {
        widthRaw,
        heightRaw,
        unit: leftUnit || rightUnit,
      };
    }

    return null;
  };

  const normalizeImageUrl = (url) => {
    if (!url) return '';
    if (url.indexOf('//') === 0) return `https:${url}`;
    return url;
  };

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Unable to load crop image.'));
      image.src = normalizeImageUrl(src);
    });

  const canvasToBlob = (canvas, type, quality) =>
    new Promise((resolve, reject) => {
      try {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Unable to export crop image.'));
            }
          },
          type,
          quality
        );
      } catch (error) {
        reject(error);
      }
    });

  const formatMoney = (cents) => {
    const settings = window.themeVariables && window.themeVariables.settings ? window.themeVariables.settings : {};
    const format = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
    const formatString = format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    const placeholder = match ? match[1] : 'amount';

    const formatWithDelimiters = (amount, precision = 2, thousands = ',', decimal = '.') => {
      const number = (amount / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    };

    let value;
    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  };

  class CustomSizePricingController {
    constructor(root) {
      this.root = root;
      this.formId = root.getAttribute('form-id');
      this.productForm = document.getElementById(this.formId);

      if (!this.productForm) {
        this.root.hidden = true;
        return;
      }

      this.config = this.readJson('[data-custom-size-pricing-config]');
      this.variants = this.readJson('[data-custom-size-pricing-variants]') || [];
      this.images = (this.readJson('[data-custom-size-pricing-images]') || []).filter((image) => image && image.src);
      this.messages = (this.config && this.config.messages) || {};
      this.cropSource = root.dataset.cropSource || 'product-media';
      this.customerUploadMode = this.cropSource === customerUploadSource;
      this.pricingEnabled = root.dataset.customSizePricingEnabled !== 'false';

      if (!this.config || (!this.config.enabled && !this.customerUploadMode)) {
        this.root.hidden = true;
        return;
      }

      this.body = this.query('[data-custom-size-body]');
      this.widthInput = this.query('[data-custom-size-width]');
      this.heightInput = this.query('[data-custom-size-height]');
      this.unitSelect = this.query('[data-custom-size-unit]');
      this.message = this.query('[data-custom-size-message]');
      this.warning = this.query('[data-custom-size-warning]');
      this.result = this.query('[data-custom-size-result]');
      this.priceOutput = this.query('[data-custom-size-price]');
      this.materialOutput = this.query('[data-custom-size-material]');
      this.seamNotice = this.query('[data-custom-size-seam-notice]');
      this.seamOpenButton = this.query('[data-custom-size-seam-open]');
      this.seamThumbnail = this.query('[data-custom-size-seam-thumb]');
      this.seamText = this.query('[data-custom-size-seam-text]');
      this.seamModal = this.query('[data-custom-size-seam-modal]');
      this.seamModalImage = this.query('[data-custom-size-seam-modal-image]');
      this.cropSection = this.query('[data-custom-size-crop]');
      this.cropOpenButton = this.query('[data-custom-size-crop-open]');
      this.cropStatus = this.query('[data-custom-size-crop-status]');
      this.cropPreview = this.query('[data-custom-size-crop-preview]');
      this.cropPreviewImage = this.query('[data-custom-size-crop-preview-image]');
      this.cropRemoveButton = this.query('[data-custom-size-crop-remove]');
      this.cropModal = this.query('[data-custom-size-crop-modal]');
      this.cropCanvas = this.query('[data-custom-size-crop-canvas]');
      this.cropCanvasContext = this.cropCanvas ? this.cropCanvas.getContext('2d') : null;
      this.cropThumbs = this.query('[data-custom-size-crop-thumbs]');
      this.cropModalMessage = this.query('[data-custom-size-crop-modal-message]');
      this.cropConfirmButton = this.query('[data-custom-size-crop-confirm]');
      this.cropFileInput = this.query('[data-custom-size-crop-file]');
      this.cropModalTitle = this.query('[data-custom-size-crop-title]');
      this.uploadSection = this.query('[data-custom-size-upload]');
      this.uploadList = this.query('[data-custom-size-upload-list]');
      this.uploadInput = this.query('[data-custom-size-upload-input]');
      this.uploadButton = this.query('[data-custom-size-upload-button]');
      this.uploadHint = this.query('[data-custom-size-upload-hint]');
      this.uploadFileInputs = Array.from(this.root.querySelectorAll('[data-custom-size-upload-file]'));
      this.guideOpenButton = this.query('[data-custom-size-guide-open]');
      this.guideModal = this.query('[data-custom-size-guide-modal]');
      this.hiddenFields = {
        flag: this.query('[data-custom-size-flag]'),
        rule: this.query('[data-custom-size-rule]'),
        width: this.query('[data-custom-size-property-width]'),
        height: this.query('[data-custom-size-property-height]'),
        unit: this.query('[data-custom-size-property-unit]'),
        crop: this.query('[data-custom-size-property-crop]'),
        price: this.query('[data-custom-size-property-price]'),
      };
      this.currentVariant = this.findVariantById(this.getCurrentVariantId()) || this.findVariantById(this.root.dataset.initialVariantId);
      this.currentDimensionSignature = '';
      this.currentSeamNotice = null;
      this.seamModalOpen = false;
      this.currentCropDimensionLabel = '';
      this.cropAspectRatio = null;
      this.cropFile = null;
      this.cropPreviewUrl = '';
      this.cropModalOpen = false;
      this.activeCropImageIndex = 0;
      this.cropImageElement = null;
      this.cropImageLoadingId = 0;
      this.cropCanvasSize = { width: 0, height: 0 };
      this.cropImageRect = null;
      this.cropRect = null;
      this.cropDrag = null;
      this.cropPointers = new Map();
      this.cropPinch = null;
      this.uploadItems = [];
      this.uploadEnabled = false;
      this.fixedVariantDimension = null;
      this.lastProcessedSizeOptionValue = null;
      this.guideModalOpen = false;
      this.boundResizeCropCanvas = this.resizeCropCanvas.bind(this);
      this.boundDocumentKeyDown = this.onDocumentKeyDown.bind(this);

      if (this.unitSelect && this.config.defaultUnit) {
        this.unitSelect.value = this.config.defaultUnit;
      }

      if (this.customerUploadMode && this.cropModalTitle) {
        this.cropModalTitle.textContent = this.getMessage('cropUploadedImage', 'Crop Uploaded Image');
      }

      this.renderCropThumbnails();
      this.renderCustomerUploads();
      this.bindEvents();
      this.updateCropUi(false);
      this.update();
    }

    query(selector) {
      return this.root.querySelector(selector);
    }

    getMessage(key, fallback) {
      return this.messages && this.messages[key] ? this.messages[key] : fallback;
    }

    readJson(selector) {
      const script = this.query(selector);
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.warn('Unable to parse custom size pricing JSON.', error);
        return null;
      }
    }

    bindEvents() {
      this.widthInput.addEventListener('input', this.update.bind(this));
      this.heightInput.addEventListener('input', this.update.bind(this));
      this.unitSelect.addEventListener('change', this.update.bind(this));
      this.productForm.addEventListener('variant:changed', this.onVariantChanged.bind(this));
      this.productForm.addEventListener('formdata', this.onFormData.bind(this));

      if (this.cropOpenButton) {
        this.cropOpenButton.addEventListener('click', this.openCropModal.bind(this));
      }

      if (this.seamOpenButton) {
        this.seamOpenButton.addEventListener('click', this.openSeamModal.bind(this));
      }

      if (this.cropRemoveButton) {
        this.cropRemoveButton.addEventListener('click', () => { this.productionImageError=false; this.clearCropSelection(); });
      }

      if (this.uploadInput) {
        this.uploadInput.addEventListener('change', this.onUploadInputChange.bind(this));
      }

      if (this.guideOpenButton) {
        this.guideOpenButton.addEventListener('click', this.openGuideModal.bind(this));
      }

      this.root.querySelectorAll('[data-custom-size-seam-close]').forEach((button) => {
        button.addEventListener('click', this.closeSeamModal.bind(this));
      });

      this.root.querySelectorAll('[data-custom-size-crop-close]').forEach((button) => {
        button.addEventListener('click', this.closeCropModal.bind(this));
      });

      this.root.querySelectorAll('[data-custom-size-guide-close]').forEach((button) => {
        button.addEventListener('click', this.closeGuideModal.bind(this));
      });

      if (this.guideModal) {
        this.guideModal.addEventListener('click', (event) => {
          if (event.target === this.guideModal) {
            this.closeGuideModal();
          }
        });
      }

      if (this.cropConfirmButton) {
        this.cropConfirmButton.addEventListener('click', this.confirmCrop.bind(this));
      }

      if (this.cropCanvas) {
        this.cropCanvas.addEventListener('pointerdown', this.onCropPointerDown.bind(this));
        this.cropCanvas.addEventListener('pointermove', this.onCropPointerMove.bind(this));
        this.cropCanvas.addEventListener('pointerup', this.onCropPointerUp.bind(this));
        this.cropCanvas.addEventListener('pointercancel', this.onCropPointerUp.bind(this));
        this.cropCanvas.addEventListener('wheel', this.onCropWheel.bind(this), { passive: false });
      }

      window.addEventListener('resize', this.boundResizeCropCanvas);
      document.addEventListener('keydown', this.boundDocumentKeyDown);

      const productVariants = this.getProductVariantsElement();
      if (productVariants) {
        productVariants.addEventListener('change', this.onProductOptionsChanged.bind(this));
        productVariants.addEventListener('input', this.onProductOptionsChanged.bind(this));
      }

      const masterSelector = this.productForm.querySelector('input[name="id"]');
      if (masterSelector) {
        masterSelector.addEventListener('change', () => {
          this.currentVariant = this.findVariantById(masterSelector.value);
          this.update();
        });
      }

      this.paymentButtonObserver = new MutationObserver(() => {
        this.updateDynamicCheckoutVisibility(this.isCustomVariantSelected() || this.isUploadOnlyMode());
      });
      this.paymentButtonObserver.observe(this.productForm, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    getCurrentVariantId() {
      const input = this.productForm.querySelector('input[name="id"]');
      return input ? input.value : this.root.dataset.initialVariantId;
    }

    findVariantById(id) {
      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    findVariantBySelectedOptions() {
      const selectedOptionValues = this.getSelectedOptionValues();
      if (!selectedOptionValues.length) return null;

      return (
        this.variants.find((variant) => {
          const variantOptions = Array.isArray(variant.options)
            ? variant.options
            : [variant.option1, variant.option2, variant.option3].filter(Boolean);

          return selectedOptionValues.every((value, index) => String(variantOptions[index]) === String(value));
        }) || null
      );
    }

    onVariantChanged(event) {
      this.currentVariant = event.detail && event.detail.variant ? event.detail.variant : this.findVariantById(this.getCurrentVariantId());
      this.update();
    }

    onProductOptionsChanged() {
      const selected = this.findVariantBySelectedOptions();
      const picker = this.getProductVariantsElement();
      if (selected && picker && picker.product && picker.masterSelector &&
          String(selected.id) !== String(this.getCurrentVariantId())) {
        // Use the theme's own transition so price/media/URL listeners also run.
        picker.selectVariant(selected.id);
      }
      this.currentVariant = selected || this.findVariantById(this.getCurrentVariantId()) || this.currentVariant;
      this.update();
    }

    onDocumentKeyDown(event) {
      if (event.key !== 'Escape') return;

      if (this.seamModalOpen) {
        this.closeSeamModal();
        return;
      }

      if (this.cropModalOpen) {
        this.closeCropModal();
        return;
      }

      if (this.guideModalOpen) {
        this.closeGuideModal();
      }
    }

    onFormData(event) {
      if (!event.formData) return;
      if (this.isMirrored() && !this.productForm.mirroredBackdrop.usesOriginalCustomFlow()) { this.productForm.mirroredBackdrop.serialize(event.formData); return; }
      this.productForm.mirroredBackdrop?.serialize(event.formData);

      // Serialize the current selection directly. Do not render from formdata:
      // payment widgets also read forms while observing DOM changes.
      this.currentVariant = this.findVariantBySelectedOptions() || this.findVariantById(this.getCurrentVariantId());
      const purchaseInput = this.productForm.querySelector('input[name="id"]');
      if (purchaseInput && this.currentVariant && purchaseInput.value !== String(this.currentVariant.id)) {
        purchaseInput.value = String(this.currentVariant.id);
      }
      event.formData.set('id', this.currentVariant ? String(this.currentVariant.id) : '');
      this.deleteCustomSizeFormData(event.formData);
      if (!this.isUploadOnlyMode() && this.isCustomSizePricingSelected()) {
        const width = this.widthInput.value.trim();
        const height = this.heightInput.value.trim();
        const unit = this.unitSelect.value;
        const material = this.getMaterialRule();
        const dimensions = this.getNormalizedDimensions(Number(width), Number(height), unit);
        if (!this.validate(width, height, unit, material, dimensions)) {
          const values = {
            _custom_size: '1', _custom_size_rule: this.config.version || '',
            'Custom Width': `${width} ${unit}`, 'Custom Height': `${height} ${unit}`,
            _custom_unit: unit, _frontend_calculated_price: this.calculatePrice(dimensions, material).toFixed(2),
            'Crop Preference': this.getCropPreferenceValue(),
          };
          Object.entries(values).forEach(([key, value]) => event.formData.set(`properties[${key}]`, value));
        }
      }
      const submissionError = this.validateSubmission(event.formData);
      if (submissionError) {
        // FormData events cannot be cancelled. Invalidate the purchase ID so no
        // consumer can submit a standard variant with a custom-size quote.
        event.formData.delete('id');
        // Normal input handlers render validation; a background form read
        // must not show a purchase notification or trigger DOM observers.
        return;
      }

      this.currentVariant = this.findVariantById(event.formData.get('id'));
      event.formData.delete(cropImagePropertyName);
      event.formData.delete('properties[_crop_data]');
      const targetVariantSelected = this.isCustomVariantSelected();
      const customSizePricingSelected = this.isCustomSizePricingSelected();
      if (customSizePricingSelected || this.isCustomerUploadMode()) {
        event.formData.set('properties[Image Orientation]', (this.confirmedCropData?.mirrored ?? this.isMirrored()) ? 'Mirrored' : 'Standard');
      }

      if (!customSizePricingSelected) {
        this.deleteCustomSizeFormData(event.formData);
      }

      const hasCustomDimensions = Boolean(
        event.formData.get('properties[Custom Width]') && event.formData.get('properties[Custom Height]')
      );
      const hasCustomerUpload =
        (targetVariantSelected || this.isUploadOnlyMode()) &&
        this.isCustomerUploadMode() &&
        this.uploadEnabled &&
        this.uploadItems.length > 0;

      if (this.isCustomerUploadMode()) {
        for (let index = 0; index < maxCustomerUploadImages; index += 1) {
          event.formData.delete(getCustomerUploadPropertyName(index));
          event.formData.delete(`properties[Preview Image ${index + 1}]`);
          event.formData.delete(`properties[_image_edit_${index + 1}]`);
        }

        if (this.uploadEnabled) {
          this.uploadItems.slice(0, maxCustomerUploadImages).forEach((item, index) => {
            event.formData.append(getCustomerUploadPropertyName(index), item.originalFile, item.originalFile.name);
            if (item.previewFile && item.editData) {
              event.formData.append(`properties[Preview Image ${index + 1}]`, item.previewFile, item.previewFile.name);
              event.formData.set(`properties[_image_edit_${index + 1}]`, JSON.stringify(item.editData));
            }
          });
        }

        if (hasCustomerUpload) {
          event.formData.set(cropPreferencePropertyName, this.getCropPreferenceValue());
        } else if (!hasCustomDimensions) {
          event.formData.delete(cropPreferencePropertyName);
        }

        return;
      }

      const hasCropImage = this.cropFile && this.confirmedCropData && targetVariantSelected;
      if (hasCropImage) {
        event.formData.append(cropImagePropertyName, this.cropFile, this.cropFile.name);
        if (this.confirmedCropData) {
          const {rect,mirrored}=this.confirmedCropData;
          event.formData.set('properties[_crop_data]', JSON.stringify({rect,mirrored:Boolean(mirrored)}));
          event.formData.set('properties[Original SKU]', this.getCropSourceSku());
        }
        event.formData.set(cropPreferencePropertyName, this.getCropPreferenceValue());
      }
      if (!hasCustomDimensions && !hasCropImage) {
        event.formData.delete(cropPreferencePropertyName);
      }
    }

    validateSubmission(formData) {
      const error = 'The selected size changed or its price is invalid. Please select your size again.';
      if(this.hasBackdropOrientation()&&this.productionImageError)return this.getMessage('unableToExportCrop','Unable to export the crop. Please try again.');
      const ids = formData.getAll('id');
      const variant = ids.length === 1 && this.findVariantById(ids[0]);
      const selected = this.findVariantBySelectedOptions();
      if (!variant || (selected && String(selected.id) !== String(variant.id))) return error;

      const pricingFields = customSizePropertyNames.filter((name) => name !== cropPreferencePropertyName);
      const hasPricing = pricingFields.some((name) => String(formData.get(name) || '').trim());
      if (this.isUploadOnlyMode() || !this.isCustomSizePricingSelected(variant)) {
        return hasPricing ? error : '';
      }

      const material = (this.config.materials || []).find((rule) =>
        String(rule.sku || '').trim().toUpperCase() === String(variant.sku || '').trim().toUpperCase());
      const width = this.widthInput.value.trim();
      const height = this.heightInput.value.trim();
      const unit = this.unitSelect.value;
      const dimensions = this.getNormalizedDimensions(Number(width), Number(height), unit);
      if (this.validate(width, height, unit, material, dimensions)) return error;
      const price = this.calculatePrice(dimensions, material);
      if (!Number.isFinite(price) || price <= 0) return error;
      const expected = {
        'properties[_custom_size]': '1',
        'properties[_custom_size_rule]': this.config.version || '',
        'properties[Custom Width]': `${width} ${unit}`,
        'properties[Custom Height]': `${height} ${unit}`,
        'properties[_custom_unit]': unit,
        'properties[_frontend_calculated_price]': price.toFixed(2),
      };
      return Object.entries(expected).every(([key, value]) =>
        formData.getAll(key).length === 1 && formData.get(key) === value) ? '' : error;
    }

    isCustomerUploadMode() {
      return this.customerUploadMode;
    }

    isUploadOnlyMode() {
      return this.isCustomerUploadMode() && !this.pricingEnabled;
    }

    getProductVariantsElement() {
      const variants = Array.from(document.querySelectorAll('product-variants'));
      return (
        variants.find((element) => element.getAttribute('form-id') === this.formId && element.classList.contains('product-form__variants')) ||
        variants.find((element) => element.getAttribute('form-id') === this.formId)
      );
    }

    getSelectedOptionValues() {
      const productVariants = this.getProductVariantsElement();
      if (!productVariants) return [];

      const values = [];

      productVariants.querySelectorAll('select[name^="option"]').forEach((select) => {
        if (select.value) values.push(select.value);
      });

      productVariants.querySelectorAll('input[name^="option"]:checked').forEach((input) => {
        if (input.value) values.push(input.value);
      });

      return values.filter(Boolean);
    }

    getCurrentSizeOptionValue(variant = this.currentVariant) {
      if (!variant) return '';

      const options = Array.isArray(variant.options)
        ? variant.options
        : [variant.option1, variant.option2, variant.option3].filter((option) => typeof option !== 'undefined');

      return options.length ? String(options[0] || '').trim() : '';
    }

    syncFixedVariantDimension(customSizeVisible) {
      if (!customSizeVisible) {
        this.fixedVariantDimension = null;
        this.lastProcessedSizeOptionValue = null;
        return null;
      }

      const sizeOptionValue = this.getCurrentSizeOptionValue();
      const variantTitle = String((this.currentVariant && this.currentVariant.title) || '').trim();
      const dimensionSourceSignature = `${sizeOptionValue}\n${variantTitle}`;
      if (this.lastProcessedSizeOptionValue === dimensionSourceSignature) {
        return this.fixedVariantDimension;
      }

      const hadPreviousSizeOption = this.lastProcessedSizeOptionValue !== null;
      const fixedVariantDimension =
        parseVariantDimension(sizeOptionValue) ||
        parseVariantDimension(variantTitle);

      this.lastProcessedSizeOptionValue = dimensionSourceSignature;
      this.fixedVariantDimension = fixedVariantDimension;

      if (hadPreviousSizeOption && !fixedVariantDimension) {
        this.widthInput.value = '';
        this.heightInput.value = '';
        this.unitSelect.value = this.config.defaultUnit || 'ft';
      }

      return fixedVariantDimension;
    }

    hasTargetVariantSku(variant = this.currentVariant) {
      const targetSkus = Array.isArray(this.config.targetVariantSkus) ? this.config.targetVariantSkus : [];
      const currentSku = variant && variant.sku ? String(variant.sku).trim().toUpperCase() : '';

      return targetSkus.some((sku) => currentSku && currentSku === String(sku).trim().toUpperCase());
    }

    hasBackdropOrientation() { return Boolean(this.productForm.mirroredBackdrop); }

    isMirrored() { return this.productForm.mirroredBackdrop?.mirrored === true; }

    isCustomVariantSelected() {
      return this.pricingEnabled && (this.hasTargetVariantSku() || this.hasBackdropOrientation());
    }

    isFixedVariantSelected(variant = this.currentVariant) {
      if (!this.hasTargetVariantSku(variant)) return false;

      return Boolean(
        parseVariantDimension(this.getCurrentSizeOptionValue(variant)) ||
          parseVariantDimension(variant && variant.title)
      );
    }

    isCustomSizePricingSelected(variant = this.currentVariant) {
      return this.pricingEnabled && this.hasTargetVariantSku(variant) && !this.isFixedVariantSelected(variant);
    }

    setDimensionInputsState(options = {}) {
      const { editable = true, widthRaw = '', heightRaw = '', unit = this.config.defaultUnit || 'ft' } = options;

      if (this.widthInput) {
        this.widthInput.disabled = !editable;
        this.widthInput.readOnly = !editable;
        if (editable) {
          this.widthInput.removeAttribute('aria-readonly');
          this.widthInput.removeAttribute('aria-disabled');
        } else {
          this.widthInput.setAttribute('aria-readonly', 'true');
          this.widthInput.setAttribute('aria-disabled', 'true');
        }
        if (!editable || widthRaw !== '') {
          this.widthInput.value = widthRaw;
        }
      }

      if (this.heightInput) {
        this.heightInput.disabled = !editable;
        this.heightInput.readOnly = !editable;
        if (editable) {
          this.heightInput.removeAttribute('aria-readonly');
          this.heightInput.removeAttribute('aria-disabled');
        } else {
          this.heightInput.setAttribute('aria-readonly', 'true');
          this.heightInput.setAttribute('aria-disabled', 'true');
        }
        if (!editable || heightRaw !== '') {
          this.heightInput.value = heightRaw;
        }
      }

      if (this.unitSelect) {
        this.unitSelect.disabled = !editable;
        if (editable) {
          this.unitSelect.removeAttribute('aria-disabled');
        } else {
          this.unitSelect.setAttribute('aria-disabled', 'true');
        }
        if (!editable || unit) {
          this.unitSelect.value = unit;
        }
      }
    }

    getMaterialKey() {
      const currentSku = this.currentVariant && this.currentVariant.sku ? String(this.currentVariant.sku).trim().toUpperCase() : '';
      const materials = Array.isArray(this.config.materials) ? this.config.materials : [];
      const material = materials.find((rule) => currentSku && String(rule.sku || '').trim().toUpperCase() === currentSku);
      return material ? material.id : null;
    }

    getMaterialRule(materialKey = this.getMaterialKey()) {
      const materials = Array.isArray(this.config.materials) ? this.config.materials : [];
      return materialKey ? materials.find((material) => material.id === materialKey) || null : null;
    }

    getNormalizedDimensions(width, height, unit) {
      const feetToMeter = this.config.feetToMeter || 0.3048;

      if (unit === 'ft') {
        const widthM = ceilTo(width * feetToMeter, 2);
        const heightM = ceilTo(height * feetToMeter, 2);
        return {
          widthM,
          heightM,
          areaM2: roundTo(widthM * heightM, 2),
        };
      }

      if (unit === 'm') {
        return {
          widthM: width,
          heightM: height,
          areaM2: roundTo(width * height, 2),
        };
      }

      return null;
    }

    getDimensionsInFeet(width, height, unit) {
      const feetToMeter = Number(this.config.feetToMeter || 0.3048);

      if (unit === 'ft') {
        return {
          widthFt: width,
          heightFt: height,
        };
      }

      if (unit === 'm' && feetToMeter > 0) {
        return {
          widthFt: width / feetToMeter,
          heightFt: height / feetToMeter,
        };
      }

      return null;
    }

    calculatePrice(dimensions, materialRule) {
      const rawPrice =
        dimensions.areaM2 * Number(materialRule.pricePerSquareMeter) + Number(materialRule.fixedFee);
      const minimumPrice = Number(materialRule.minimumPrice || 0);
      return rawPrice < minimumPrice ? roundTo(minimumPrice, 2) : Math.round(rawPrice);
    }

    validate(widthRaw, heightRaw, unit, materialRule, dimensions) {
      if (!materialRule) return this.getMessage('unsupportedMaterial', 'Selected material is not available for custom size pricing.');
      if (!widthRaw || !heightRaw) return this.getMessage('invalidDimensions', 'Please enter a valid custom width and height.');
      if (!this.config.allowedUnits || !this.config.allowedUnits.includes(unit)) return this.getMessage('invalidUnit', 'Please select a valid unit.');
      if (!dimensions || dimensions.widthM <= 0 || dimensions.heightM <= 0) return this.getMessage('invalidDimensions', 'Please enter a valid custom width and height.');
      if (!hasMaxTwoDecimals(widthRaw) || !hasMaxTwoDecimals(heightRaw)) return this.getMessage('tooManyDecimals', 'Width and height can use up to 2 decimal places.');
      return '';
    }

    clearHiddenFields() {
      Object.keys(this.hiddenFields).forEach((key) => {
        if (this.hiddenFields[key]) {
          this.hiddenFields[key].value = '';
          this.hiddenFields[key].disabled = true;
        }
      });
    }

    deleteCustomSizeFormData(formData) {
      customSizePropertyNames.forEach((propertyName) => {
        formData.delete(propertyName);
      });
    }

    updateHiddenFields(widthRaw, heightRaw, unit, materialRule, dimensions, price) {
      Object.keys(this.hiddenFields).forEach((key) => {
        if (this.hiddenFields[key]) {
          this.hiddenFields[key].disabled = false;
        }
      });
      this.hiddenFields.flag.value = '1';
      this.hiddenFields.rule.value = this.config.version || '';
      this.hiddenFields.width.value = `${widthRaw} ${unit}`;
      this.hiddenFields.height.value = `${heightRaw} ${unit}`;
      this.hiddenFields.unit.value = unit;
      this.hiddenFields.crop.value = this.getCropPreferenceValue();
      this.hiddenFields.price.value = price.toFixed(2);
    }

    setMessage(text) {
      this.message.textContent = text || '';
      this.message.hidden = !text;
    }

    setWarning(text) {
      this.warning.textContent = text || '';
      this.warning.hidden = !text;
    }

    getSeamNotice(materialKey) {
      const seamNotices = this.config && this.config.seamNotices ? this.config.seamNotices : {};
      return materialKey && seamNotices[materialKey] ? seamNotices[materialKey] : null;
    }

    shouldShowSeamNotice(materialKey, width, height, unit) {
      const notice = this.getSeamNotice(materialKey);
      const thresholdFt = notice ? Number(notice.thresholdFt) : null;
      const dimensionsFt = this.getDimensionsInFeet(width, height, unit);

      return Boolean(
        notice &&
          Number.isFinite(thresholdFt) &&
          thresholdFt > 0 &&
          dimensionsFt &&
          dimensionsFt.widthFt > thresholdFt &&
          dimensionsFt.heightFt > thresholdFt
      );
    }

    updateSeamNotice(materialKey) {
      const notice = this.getSeamNotice(materialKey);
      const imageUrl = notice ? normalizeImageUrl(notice.imageUrl || notice.image || '') : '';
      const imageAlt = notice && notice.alt ? notice.alt : 'Stitching seam example';
      const imageWidth = notice && notice.imageWidth ? String(notice.imageWidth) : '';
      const imageHeight = notice && notice.imageHeight ? String(notice.imageHeight) : '';

      this.currentSeamNotice = notice;

      if (this.seamNotice) {
        this.seamNotice.hidden = !notice;
      }

      if (this.seamText) {
        this.seamText.textContent = notice && notice.text ? String(notice.text).replace(/\\n/g, '\n') : '';
      }

      if (this.seamOpenButton) {
        this.seamOpenButton.hidden = !imageUrl;
        this.seamOpenButton.setAttribute('aria-expanded', this.seamModalOpen ? 'true' : 'false');
      }

      if (this.seamThumbnail) {
        if (imageUrl) {
          this.seamThumbnail.src = imageUrl;
        } else {
          this.seamThumbnail.removeAttribute('src');
        }
        this.seamThumbnail.alt = imageAlt;
        if (imageWidth && imageHeight) {
          this.seamThumbnail.setAttribute('width', imageWidth);
          this.seamThumbnail.setAttribute('height', imageHeight);
        }
      }

      if (this.seamModalImage) {
        if (imageUrl) {
          this.seamModalImage.src = imageUrl;
        } else {
          this.seamModalImage.removeAttribute('src');
        }
        this.seamModalImage.alt = imageAlt;
        if (imageWidth && imageHeight) {
          this.seamModalImage.setAttribute('width', imageWidth);
          this.seamModalImage.setAttribute('height', imageHeight);
        }
      }

      if (!notice && this.seamModalOpen) {
        this.closeSeamModal();
      }
    }

    openSeamModal() {
      if (!this.currentSeamNotice || !this.seamModal) return;

      const imageUrl = normalizeImageUrl(this.currentSeamNotice.imageUrl || this.currentSeamNotice.image || '');
      if (!imageUrl) return;

      if (this.seamModalImage) {
        this.seamModalImage.src = imageUrl;
        this.seamModalImage.alt = this.currentSeamNotice.alt || 'Stitching seam example';
        if (this.currentSeamNotice.imageWidth && this.currentSeamNotice.imageHeight) {
          this.seamModalImage.setAttribute('width', String(this.currentSeamNotice.imageWidth));
          this.seamModalImage.setAttribute('height', String(this.currentSeamNotice.imageHeight));
        }
      }

      this.seamModal.hidden = false;
      this.seamModalOpen = true;

      if (this.seamOpenButton) {
        this.seamOpenButton.setAttribute('aria-expanded', 'true');
      }
    }

    closeSeamModal() {
      if (!this.seamModal) return;

      this.seamModal.hidden = true;
      this.seamModalOpen = false;

      if (this.seamOpenButton) {
        this.seamOpenButton.setAttribute('aria-expanded', 'false');
      }
    }

    openGuideModal() {
      if (!this.guideModal) return;

      this.guideModal.hidden = false;
      this.guideModalOpen = true;
      document.documentElement.classList.add('custom-size-pricing-guide-open');

      if (this.guideOpenButton) {
        this.guideOpenButton.setAttribute('aria-expanded', 'true');
      }
    }

    closeGuideModal() {
      if (!this.guideModal) return;

      this.guideModal.hidden = true;
      this.guideModalOpen = false;
      document.documentElement.classList.remove('custom-size-pricing-guide-open');

      if (this.guideOpenButton) {
        this.guideOpenButton.setAttribute('aria-expanded', 'false');
      }
    }

    updateAddToCartButton(disabled) {
      const buttons = this.productForm.querySelectorAll('button[type="submit"], [type="submit"][data-product-add-to-cart-button]');
      buttons.forEach((button) => {
        if (button.closest('.shopify-payment-button')) return;

        if (disabled) {
          if (!button.disabled) {
            button.dataset.customSizeDisabled = 'true';
            button.setAttribute('disabled', 'disabled');
          }
        } else if (button.dataset.customSizeDisabled === 'true') {
          button.removeAttribute('disabled');
          delete button.dataset.customSizeDisabled;
        }
      });
    }

    updateDynamicCheckoutVisibility(hidden) {
      const paymentButtons = this.productForm.querySelectorAll('.shopify-payment-button');
      paymentButtons.forEach((element) => {
        if (hidden) {
          if (element.dataset.customSizeHidden !== 'true') {
            element.dataset.customSizePreviousDisplay = element.style.display || '';
            element.dataset.customSizeHidden = 'true';
          }
          if (element.style.display !== 'none') {
            element.style.display = 'none';
          }
        } else if (element.dataset.customSizeHidden === 'true') {
          element.style.display = element.dataset.customSizePreviousDisplay || '';
          delete element.dataset.customSizePreviousDisplay;
          delete element.dataset.customSizeHidden;
        }
      });
    }

    updatePriceDisplays(formattedPrice) {
      const salePriceLabel = window.themeVariables && window.themeVariables.strings ? window.themeVariables.strings.productSalePrice : 'Sale price';
      const priceHtml = `<span class="price price--large"><span class="visually-hidden">${salePriceLabel}</span>${formattedPrice}</span>`;

      document.querySelectorAll(`product-meta[form-id="${this.formId}"] [data-product-price-list]`).forEach((priceList) => {
        priceList.innerHTML = priceHtml;
        priceList.style.display = '';
      });

      document.querySelectorAll(`product-sticky-form[form-id="${this.formId}"] .product-sticky-form__price`).forEach((priceElement) => {
        priceElement.innerHTML = formattedPrice;
      });
    }

    restoreVariantPriceDisplays() {
      if (this.priceOutput) {
        this.priceOutput.innerHTML = '';
      }

      const variant = this.currentVariant;
      const variantPrice = variant && typeof variant.price !== 'undefined' ? toNumber(variant.price) : null;
      if (!variant || variantPrice === null) {
        this.clearPriceDisplays();
        return;
      }

      const price = Math.round(variantPrice);
      const compareAtPrice =
        typeof variant.compare_at_price !== 'undefined' && variant.compare_at_price !== null
          ? toNumber(variant.compare_at_price)
          : null;
      const formattedPrice = formatMoney(price);
      const salePriceLabel =
        window.themeVariables && window.themeVariables.strings
          ? window.themeVariables.strings.productSalePrice
          : 'Sale price';
      const regularPriceLabel =
        window.themeVariables && window.themeVariables.strings
          ? window.themeVariables.strings.productRegularPrice
          : 'Regular price';

      document.querySelectorAll(`product-meta[form-id="${this.formId}"]`).forEach((productMeta) => {
        const priceList = productMeta.querySelector('[data-product-price-list]');
        if (!priceList) return;

        const priceClass = productMeta.getAttribute('price-class') || '';
        if (compareAtPrice !== null && compareAtPrice > price) {
          priceList.innerHTML =
            `<span class="price price--highlight ${priceClass}"><span class="visually-hidden">${salePriceLabel}</span>${formattedPrice}</span>` +
            `<span class="price price--compare"><span class="visually-hidden">${regularPriceLabel}</span>${formatMoney(
              Math.round(compareAtPrice)
            )}</span>`;
        } else {
          priceList.innerHTML = `<span class="price ${priceClass}"><span class="visually-hidden">${salePriceLabel}</span>${formattedPrice}</span>`;
        }

        const unitPrice = typeof variant.unit_price !== 'undefined' ? toNumber(variant.unit_price) : null;
        if (variant.unit_price_measurement && unitPrice !== null) {
          const measurement = variant.unit_price_measurement;
          const referenceValue =
            measurement.reference_value !== 1
              ? `<span class="unit-price-measurement__reference-value">${measurement.reference_value}</span>`
              : '';
          priceList.innerHTML += `
            <div class="price text--subdued ${productMeta.getAttribute('unit-price-class') || ''}">
              <div class="unit-price-measurement">
                <span class="unit-price-measurement__price">${formatMoney(Math.round(unitPrice))}</span>
                <span class="unit-price-measurement__separator">/</span>
                ${referenceValue}
                <span class="unit-price-measurement__reference-unit">${measurement.reference_unit}</span>
              </div>
            </div>
          `;
        }

        priceList.style.display = '';
      });

      document.querySelectorAll(`product-sticky-form[form-id="${this.formId}"] .product-sticky-form__price`).forEach((priceElement) => {
        priceElement.innerHTML = formattedPrice;
      });
    }

    clearPriceDisplays() {
      if (this.priceOutput) {
        this.priceOutput.innerHTML = '';
      }

      document.querySelectorAll(`product-meta[form-id="${this.formId}"] [data-product-price-list]`).forEach((priceList) => {
        priceList.innerHTML = '';
        priceList.style.display = 'none';
      });

      document.querySelectorAll(`product-sticky-form[form-id="${this.formId}"] .product-sticky-form__price`).forEach((priceElement) => {
        priceElement.innerHTML = '';
      });
    }

    getDimensionSignature(widthRaw, heightRaw, unit) {
      return `${widthRaw}|${heightRaw}|${unit}`;
    }

    formatCropDimensionValue(value) {
      const number = toNumber(value);
      if (number === null) return '';

      const rounded = roundTo(number, 2);
      return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace(/0+$/, '').replace(/\.$/, '');
    }

    getCropDimensionLabel(widthRaw, heightRaw, unit) {
      const widthLabel = this.formatCropDimensionValue(widthRaw);
      const heightLabel = this.formatCropDimensionValue(heightRaw);
      const unitLabel = unit ? String(unit).trim() : '';

      return widthLabel && heightLabel && unitLabel ? `${widthLabel}*${heightLabel}${unitLabel}` : '';
    }

    setCropDimensionLabel(widthRaw, heightRaw, unit) {
      this.currentCropDimensionLabel = this.getCropDimensionLabel(widthRaw, heightRaw, unit);

      if (this.cropModalOpen) {
        this.drawCropCanvas();
      }
    }

    clearCropDimensionLabel() {
      this.currentCropDimensionLabel = '';

      if (this.cropModalOpen) {
        this.drawCropCanvas();
      }
    }

    setCropPreference(value) {
      if (this.hiddenFields.crop) {
        this.hiddenFields.crop.value = value;
      }
    }

    getCropPreferenceValue() {
      if (this.isCustomerUploadMode()) {
        if (!this.uploadItems.length) return this.getMessage('designerAdjust', 'Designer adjust');
        return this.uploadItems.some((item) => item.cropped)
          ? this.getMessage('customerCrop', 'Customer crop')
          : this.getMessage('customerUpload', 'Customer upload');
      }

      return this.cropFile ? this.getMessage('customerCrop', 'Customer crop') : this.getMessage('designerAdjust', 'Designer adjust');
    }

    clearCropSelection(options = {}) {
      if (this.isCustomerUploadMode()) {
        this.resetCropGestureState();
        if (options.updateUi !== false) {
          this.updateUploadUi();
        }
        return;
      }

      const updateUi = options.updateUi !== false;
      this.resetCropGestureState();
      this.cropFile = null;
      this.setConfirmedCropData(null);
      this.revokeCropPreviewUrl();
      this.setCropPreference(options.resetPreference === false ? '' : this.getMessage('designerAdjust', 'Designer adjust'));
      this.setCropFileInput(null);

      if (updateUi) {
        this.updateCropStatus();
      }
    }

    revokeCropPreviewUrl() {
      if (this.cropPreviewUrl) {
        URL.revokeObjectURL(this.cropPreviewUrl);
        this.cropPreviewUrl = '';
      }

      if (this.cropPreviewImage) {
        this.cropPreviewImage.removeAttribute('src');
      }
    }

    setCropPreviewFile(file) {
      this.revokeCropPreviewUrl();

      if (!file || !this.cropPreviewImage) return;

      this.cropPreviewUrl = URL.createObjectURL(file);
      this.cropPreviewImage.src = this.cropPreviewUrl;
    }

    setCropFileInput(file) {
      if (!this.cropFileInput) return;

      this.cropFileInput.removeAttribute('name');
      this.cropFileInput.value = '';

      if (!file || typeof DataTransfer === 'undefined') return;

      try {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        this.cropFileInput.files = transfer.files;
        this.cropFileInput.setAttribute('name', cropImagePropertyName);
      } catch (error) {
        this.cropFileInput.removeAttribute('name');
        this.cropFileInput.value = '';
      }
    }

    updateCropStatus() {
      if (this.hasBackdropOrientation()) this.updateDynamicCheckoutVisibility((this.pricingEnabled && this.hasTargetVariantSku()) || this.isMirrored() || Boolean(this.cropFile));
      if (this.cropStatus) {
        const statusText = this.cropFile ? 'Crop completed' : '';
        this.cropStatus.textContent = statusText;
        this.cropStatus.hidden = !statusText;
      }

      if (this.cropOpenButton) {
        this.cropOpenButton.hidden = Boolean(this.cropFile);
      }

      if (this.cropPreview) {
        this.cropPreview.hidden = !this.cropFile;
      }
    }

    onUploadInputChange(event) {
      const files = Array.from(event.target.files || []);
      const availableSlots = Math.max(0, maxCustomerUploadImages - this.uploadItems.length);
      const acceptedFiles = files.slice(0, availableSlots);

      acceptedFiles.forEach((file) => this.addCustomerUpload(file));

      if (files.length > availableSlots) {
        this.setWarning(`You can upload up to ${maxCustomerUploadImages} images.`);
      }

      if (this.uploadInput) {
        this.uploadInput.value = '';
      }
    }

    isAcceptedCustomerUpload(file) {
      if (!file) return false;
      if (customerUploadTypes.includes(file.type)) return true;

      return /\.(jpe?g|png|webp)$/i.test(file.name || '');
    }

    addCustomerUpload(file) {
      if (!this.isAcceptedCustomerUpload(file)) {
        this.setWarning('Please upload a JPG, PNG, or WEBP image.');
        return;
      }

      if (this.uploadItems.length >= maxCustomerUploadImages) {
        this.setWarning(`You can upload up to ${maxCustomerUploadImages} images.`);
        return;
      }

      this.productionImageError=false;
      const originalUrl = URL.createObjectURL(file);
      this.productForm.mirroredBackdrop?.invalidate();
      this.uploadItems.push({
        originalFile: file,
        originalUrl,
        file,
        previewUrl: originalUrl,
        cropSelection: null,
        cropped: false,
      });
      this.setWarning('');
      this.renderCustomerUploads();
      this.syncCustomerUploadFileInputs();
    }

    renderCustomerUploads() {
      if (!this.isCustomerUploadMode() || !this.uploadList) return;

      this.uploadList.innerHTML = '';
      this.uploadItems.forEach((item, index) => {
        const wrapper = document.createElement('div');
        const cropButton = document.createElement('button');
        const image = document.createElement('img');
        const removeButton = document.createElement('button');

        wrapper.className = 'custom-size-pricing__upload-item';
        cropButton.type = 'button';
        cropButton.className = 'custom-size-pricing__upload-thumb';
        cropButton.setAttribute('aria-label', `Crop uploaded image ${index + 1}`);
        cropButton.addEventListener('click', () => this.openUploadCropModal(index));

        image.src = item.previewUrl;
        image.style.transform = this.isMirrored() !== Boolean(item.previewMirrored) ? 'scaleX(-1)' : 'none';
        image.alt = item.file && item.file.name ? item.file.name : `Uploaded image ${index + 1}`;
        image.loading = 'lazy';

        removeButton.type = 'button';
        removeButton.className = 'custom-size-pricing__upload-remove';
        removeButton.setAttribute('aria-label', `Remove uploaded image ${index + 1}`);
        removeButton.innerHTML = '&times;';
        removeButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.removeCustomerUpload(index);
        });

        cropButton.appendChild(image);
        wrapper.appendChild(cropButton);
        wrapper.appendChild(removeButton);
        this.uploadList.appendChild(wrapper);
      });

      this.updateUploadUi();
    }

    removeCustomerUpload(index) {
      const item = this.uploadItems[index];
      if (!item) return;

      if (this.cropModalOpen) this.closeCropModal();
      this.revokeCustomerUploadUrls(item);
      this.productForm.mirroredBackdrop?.invalidate();
      this.uploadItems.splice(index, 1);
      this.productionImageError=false;
      if (this.activeCropImageIndex >= this.uploadItems.length) {
        this.activeCropImageIndex = Math.max(0, this.uploadItems.length - 1);
      }

      this.renderCustomerUploads();
      this.syncCustomerUploadFileInputs();
      this.setCropPreference(this.getCropPreferenceValue());
    }

    replaceCustomerUploadWithCrop(file, selection) {
      const item = this.uploadItems[this.activeCropImageIndex];
      if (!item) return;

      const previewUrl = URL.createObjectURL(file);
      if (item.previewUrl && item.previewUrl !== item.originalUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }

      item.file = file;
      item.previewUrl = previewUrl;
      item.previewMirrored = this.isMirrored();
      item.cropSelection = selection;
      this.productForm.mirroredBackdrop?.invalidate();
      item.cropped = true;
      this.renderCustomerUploads();
      this.syncCustomerUploadFileInputs();
      this.setCropPreference(this.getCropPreferenceValue());
    }

    revokeCustomerUploadUrls(item) {
      new Set([item.originalUrl, item.previewUrl]).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    }

    clearCustomerUploads() {
      if (!this.isCustomerUploadMode()) return;

      if (this.cropModalOpen) this.closeCropModal();
      this.uploadItems.forEach((item) => this.revokeCustomerUploadUrls(item));
      this.uploadItems = [];

      if (this.uploadInput) {
        this.uploadInput.value = '';
      }

      this.renderCustomerUploads();
      this.syncCustomerUploadFileInputs();
    }

    updateUploadUi() {
      if (!this.isCustomerUploadMode()) return;

      const showUploadButton = this.uploadEnabled && this.uploadItems.length < maxCustomerUploadImages;

      if (this.uploadButton) {
        this.uploadButton.hidden = !showUploadButton;
      }

      if (this.uploadList) {
        this.uploadList.hidden = !this.uploadItems.length;
      }

      this.syncCustomerUploadFileInputs();
    }

    syncCustomerUploadFileInputs() {
      if (!this.uploadFileInputs || !this.uploadFileInputs.length) return;

      this.uploadFileInputs.forEach((input, index) => {
        input.removeAttribute('name');
        input.value = '';

        const item = this.uploadItems[index];
        if (!this.uploadEnabled || !item || !item.file || typeof DataTransfer === 'undefined') return;

        try {
          const transfer = new DataTransfer();
          transfer.items.add(item.file);
          input.files = transfer.files;
          input.setAttribute('name', getCustomerUploadPropertyName(index));
        } catch (error) {
          input.removeAttribute('name');
          input.value = '';
        }
      });
    }

    updateCropUi(canCrop) {
      if (this.isCustomerUploadMode()) {
        this.uploadEnabled = this.isUploadOnlyMode() || Boolean(canCrop && this.cropAspectRatio);

        if (this.uploadSection) {
          this.uploadSection.hidden = !this.uploadEnabled;
        }

        if (this.uploadHint) {
          this.uploadHint.hidden = !this.uploadEnabled;
        }

        if (!this.uploadEnabled && this.cropModalOpen) {
          this.closeCropModal();
        } else if (this.cropModalOpen && this.cropRectAspectRatio !== this.cropAspectRatio) {
          this.resizeCropCanvas();
        }

        this.updateUploadUi();
        return;
      }

      const enabled = Boolean(canCrop && this.images.length > 0 && this.cropAspectRatio);

      if (this.cropSection) {
        this.cropSection.hidden = this.hasBackdropOrientation() ? !this.isCustomSizePricingSelected() : !enabled;
      }

      if (this.cropOpenButton) {
        this.cropOpenButton.disabled = !enabled;
        this.cropOpenButton.setAttribute('aria-expanded', this.cropModalOpen ? 'true' : 'false');
      }

      if (!enabled && this.cropModalOpen) {
        this.closeCropModal();
      }

      this.updateCropStatus();
    }

    renderCropThumbnails() {
      if (!this.cropThumbs) return;

      if (this.isCustomerUploadMode()) {
        this.cropThumbs.hidden = true;
        return;
      }

      this.cropThumbs.innerHTML = '';
      this.images.forEach((image, index) => {
        const button = document.createElement('button');
        const thumbnail = document.createElement('img');

        button.type = 'button';
        button.className = 'custom-size-pricing__crop-thumb';
        button.setAttribute('data-custom-size-crop-thumb', '');
        button.setAttribute('aria-label', `Use image ${index + 1}`);
        button.addEventListener('click', () => this.selectCropImage(index, { clearSelection: true }));

        thumbnail.src = normalizeImageUrl(image.previewSrc || image.src);
        thumbnail.alt = image.alt || '';
        thumbnail.loading = 'lazy';
        button.appendChild(thumbnail);
        this.cropThumbs.appendChild(button);
      });

      this.updateCropThumbSelection();
    }

    updateCropThumbSelection() {
      if (!this.cropThumbs) return;
      if (this.isCustomerUploadMode()) return;

      this.cropThumbs.querySelectorAll('[data-custom-size-crop-thumb]').forEach((button, index) => {
        const selected = index === this.activeCropImageIndex;
        button.classList.toggle('is-selected', selected);
        button.toggleAttribute('aria-current', selected);
      });
    }

    openCropModal() {
      if (this.isCustomerUploadMode()) {
        this.openUploadCropModal(this.activeCropImageIndex);
        return;
      }

      if (!this.cropAspectRatio || !this.images.length || !this.cropModal) return;

      this.cropModal.hidden = false;
      this.cropModalOpen = true;

      if (this.cropOpenButton) {
        this.cropOpenButton.setAttribute('aria-expanded', 'true');
      }

      this.setCropModalMessage('');
      this.updateCropThumbSelection();
      requestAnimationFrame(() => {
        this.selectCropImage(this.activeCropImageIndex, { clearSelection: false });
      });
    }

    openUploadCropModal(index) {
      if (!this.uploadEnabled || !this.uploadItems[index] || !this.cropAspectRatio || !this.cropModal) return;

      const openId = ++this.cropImageLoadingId;
      this.activeCropImageIndex = index;
      this.cropImageElement = null;
      this.cropRect = null;
      this.cropModal.hidden = false;
      this.cropModalOpen = true;
      this.setCropModalMessage('');

      requestAnimationFrame(() => {
        if (this.cropModalOpen && openId === this.cropImageLoadingId) {
          this.selectCropImage(index, { clearSelection: false });
        }
      });
    }

    closeCropModal() {
      if (!this.cropModal) return;

      this.cropModal.hidden = true;
      this.cropModalOpen = false;
      ++this.cropImageLoadingId;
      this.resetCropGestureState();
      this.cropImageElement = null;
      this.cropRect = null;
      this.cropImageRect = null;

      if (this.cropOpenButton) {
        this.cropOpenButton.setAttribute('aria-expanded', 'false');
      }
    }

    setCropModalMessage(text) {
      if (!this.cropModalMessage) return;

      this.cropModalMessage.textContent = text || '';
      this.cropModalMessage.hidden = !text;
    }

    async selectCropImage(index, options = {}) {
      const imageData = this.getCropImageData(index);
      if (!imageData) return;

      this.resetCropGestureState();

      if (options.clearSelection && (this.isCustomerUploadMode() || this.hasBackdropOrientation())) {
        this.clearCropSelection();
      }

      this.activeCropImageIndex = index;
      if (this.hasBackdropOrientation()) {
        this.productForm.mirroredBackdrop.sourceIndex = index;
        this.productForm.mirroredBackdrop.invalidate();
      }
      this.updateCropThumbSelection();
      if (this.cropConfirmButton) this.cropConfirmButton.disabled = true;

      const loadId = ++this.cropImageLoadingId;
      const uploadItem = this.isCustomerUploadMode() ? this.uploadItems[index] : null;
      this.cropImageElement = null;
      this.cropRect = null;
      this.cropImageRect = null;

      try {
        const image = await loadImage(imageData.src);
        if (loadId !== this.cropImageLoadingId || !this.cropModalOpen ||
          (uploadItem && this.uploadItems[index] !== uploadItem)) return;

        this.cropImageElement = image;
        this.setCropModalMessage('');
        this.resizeCropCanvas();
        this.updateCropSourceAvailability();
        if (this.isMirrored() && this.confirmedCropData?.mirrored && this.cropImageRect) {
          const r=this.confirmedCropData.rect, b=this.cropImageRect;
          this.cropRect=this.clampCropRect({x:b.x+(1-r.x-r.width)*b.width,y:b.y+r.y*b.height,width:r.width*b.width,height:r.height*b.height});
          this.drawCropCanvas();
        }
        if (uploadItem && this.restoreCropSelection(uploadItem.cropSelection)) {
          this.drawCropCanvas();
        }
      } catch (error) {
        if (loadId !== this.cropImageLoadingId) return;
        this.cropImageElement = null;
        this.cropRect = null;
        this.drawEmptyCropCanvas();
        this.setCropModalMessage(this.getMessage('unableToLoadImage', 'Unable to load this image.'));
      }
    }

    getCropImageData(index) {
      if (this.isCustomerUploadMode()) {
        const item = this.uploadItems[index];
        if (!item || !item.originalUrl) return null;

        return {
          src: item.originalUrl,
          previewSrc: item.previewUrl,
          alt: item.originalFile.name || `Uploaded image ${index + 1}`,
        };
      }

      return this.images[index] || null;
    }

    getCropSourceSelection() {
      if (!this.cropImageElement || !this.cropImageRect || !this.cropRect) return null;
      const scaleX = this.cropImageElement.naturalWidth / this.cropImageRect.width;
      const scaleY = this.cropImageElement.naturalHeight / this.cropImageRect.height;
      return {
        x: (this.isMirrored() ? this.cropImageRect.width - (this.cropRect.x - this.cropImageRect.x) - this.cropRect.width : this.cropRect.x - this.cropImageRect.x) * scaleX,
        y: (this.cropRect.y - this.cropImageRect.y) * scaleY,
        width: this.cropRect.width * scaleX,
        height: this.cropRect.height * scaleY,
        aspectRatio: this.cropRectAspectRatio || this.cropAspectRatio,
      };
    }

    restoreCropSelection(selection) {
      if (!selection || !this.cropImageElement || !this.cropImageRect ||
        Math.abs(selection.aspectRatio - this.cropAspectRatio) > 0.000001) return false;
      const scaleX = this.cropImageRect.width / this.cropImageElement.naturalWidth;
      const scaleY = this.cropImageRect.height / this.cropImageElement.naturalHeight;
      this.cropRect = this.clampCropRect({
        x: this.cropImageRect.x + (this.isMirrored() ? this.cropImageElement.naturalWidth - selection.x - selection.width : selection.x) * scaleX,
        y: this.cropImageRect.y + selection.y * scaleY,
        width: selection.width * scaleX,
        height: selection.height * scaleY,
      });
      this.cropRectAspectRatio = this.cropAspectRatio;
      return true;
    }

    resizeCropCanvas() {
      if (!this.cropModalOpen || !this.cropCanvas || !this.cropCanvasContext) return;

      const selection = this.getCropSourceSelection();
      const stage = this.cropCanvas.parentElement;
      const fitStage = this.hasBackdropOrientation() && stage;
      const stageStyle = fitStage ? getComputedStyle(stage) : null;
      const cssWidth = fitStage
        ? Math.max(1, Math.floor(stage.clientWidth - parseFloat(stageStyle.paddingLeft) - parseFloat(stageStyle.paddingRight)))
        : Math.max(280, Math.floor(stage ? stage.clientWidth : 640));
      const cssHeightRatio = window.matchMedia('(max-width: 740px)').matches ? 0.86 : 0.64;
      const cssHeight = fitStage
        ? Math.max(1, Math.floor(stage.clientHeight - parseFloat(stageStyle.paddingTop) - parseFloat(stageStyle.paddingBottom)))
        : Math.max(260, Math.min(620, Math.round(cssWidth * cssHeightRatio)));
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const canvasWidth = Math.round(cssWidth * pixelRatio);
      const canvasHeight = Math.round(cssHeight * pixelRatio);

      if (this.cropCanvas.width !== canvasWidth || this.cropCanvas.height !== canvasHeight) {
        this.cropCanvas.width = canvasWidth;
        this.cropCanvas.height = canvasHeight;
        this.cropCanvas.style.width = `${cssWidth}px`;
        this.cropCanvas.style.height = `${cssHeight}px`;
      }

      this.cropCanvasContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      this.cropCanvasSize = { width: cssWidth, height: cssHeight };

      if (!this.cropImageElement) {
        this.drawEmptyCropCanvas();
        return;
      }

      this.resetCropGestureState();
      this.layoutCropCanvas(true);
      this.restoreCropSelection(selection);
      this.drawCropCanvas();
    }

    drawEmptyCropCanvas() {
      if (!this.cropCanvasContext) return;

      this.cropCanvasContext.clearRect(0, 0, this.cropCanvasSize.width, this.cropCanvasSize.height);
      this.cropCanvasContext.fillStyle = '#f6f6f6';
      this.cropCanvasContext.fillRect(0, 0, this.cropCanvasSize.width, this.cropCanvasSize.height);
    }

    fitSize(maxWidth, maxHeight, aspectRatio) {
      let width = maxWidth;
      let height = width / aspectRatio;

      if (height > maxHeight) {
        height = maxHeight;
        width = height * aspectRatio;
      }

      return { width, height };
    }

    layoutCropCanvas(resetCropRect) {
      const imageAspectRatio = this.cropImageElement.naturalWidth / this.cropImageElement.naturalHeight;
      const imageSize = this.fitSize(this.cropCanvasSize.width * 0.96, this.cropCanvasSize.height * 0.96, imageAspectRatio);
      this.cropImageRect = {
        x: (this.cropCanvasSize.width - imageSize.width) / 2,
        y: (this.cropCanvasSize.height - imageSize.height) / 2,
        width: imageSize.width,
        height: imageSize.height,
      };

      if (resetCropRect || !this.cropRect) {
        this.cropRectAspectRatio = this.cropAspectRatio;
        this.cropRect = this.getDefaultCropRect();
      } else {
        this.cropRect = this.clampCropRect(this.cropRect);
      }
    }

    getDefaultCropRect() {
      const size = this.getMaxCropSize();

      return {
        x: this.cropImageRect.x + (this.cropImageRect.width - size.width) / 2,
        y: this.cropImageRect.y + (this.cropImageRect.height - size.height) / 2,
        width: size.width,
        height: size.height,
      };
    }

    getMaxCropSize() {
      return this.fitSize(this.cropImageRect.width, this.cropImageRect.height, this.cropAspectRatio);
    }

    getMinCropWidth(maxSize = this.getMaxCropSize()) {
      return Math.min(maxSize.width, Math.max(48, maxSize.width * 0.15));
    }

    clampCropRect(rect) {
      const maxSize = this.getMaxCropSize();
      let width = Math.min(rect.width, maxSize.width);
      let height = width / this.cropAspectRatio;

      if (height > maxSize.height) {
        height = maxSize.height;
        width = height * this.cropAspectRatio;
      }

      return {
        x: clamp(rect.x, this.cropImageRect.x, this.cropImageRect.x + this.cropImageRect.width - width),
        y: clamp(rect.y, this.cropImageRect.y, this.cropImageRect.y + this.cropImageRect.height - height),
        width,
        height,
      };
    }

    scaleCropRect(factor, center, baseRect = this.cropRect) {
      if (!baseRect || !center || !Number.isFinite(factor) || factor <= 0) return;

      const maxSize = this.getMaxCropSize();
      const minWidth = this.getMinCropWidth(maxSize);
      const minHeight = minWidth / this.cropAspectRatio;
      let width = clamp(baseRect.width * factor, minWidth, maxSize.width);
      let height = width / this.cropAspectRatio;

      if (height > maxSize.height) {
        height = maxSize.height;
        width = height * this.cropAspectRatio;
      } else if (height < minHeight) {
        height = minHeight;
        width = height * this.cropAspectRatio;
      }

      this.cropRect = this.clampCropRect({
        x: center.x - width / 2,
        y: center.y - height / 2,
        width,
        height,
      });
      this.drawCropCanvas();
    }

    drawCropCanvas() {
      if (!this.cropCanvasContext || !this.cropImageElement || !this.cropImageRect || !this.cropRect) return;

      const ctx = this.cropCanvasContext;
      const canvasWidth = this.cropCanvasSize.width;
      const canvasHeight = this.cropCanvasSize.height;

      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      ctx.fillStyle = '#f6f6f6';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      ctx.save();
      const mirrorScale = Number.isFinite(this.mirrorAnimationScale) ? this.mirrorAnimationScale : (this.isMirrored() ? -1 : 1);
      if (mirrorScale !== 1) {
        const centerX = this.cropImageRect.x + this.cropImageRect.width / 2;
        ctx.translate(centerX, 0);
        ctx.scale(mirrorScale, 1);
        ctx.translate(-centerX, 0);
      }
      ctx.drawImage(
        this.cropImageElement,
        this.cropImageRect.x,
        this.cropImageRect.y,
        this.cropImageRect.width,
        this.cropImageRect.height
      );
      ctx.restore();

      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, canvasWidth, canvasHeight);
      ctx.rect(this.cropRect.x, this.cropRect.y, this.cropRect.width, this.cropRect.height);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.52)';
      ctx.fill('evenodd');
      ctx.restore();

      ctx.save();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.shadowBlur = 5;
      ctx.strokeRect(this.cropRect.x, this.cropRect.y, this.cropRect.width, this.cropRect.height);
      ctx.restore();

      this.drawCropDimensionLabel();
    }

    drawCropDimensionLabel() {
      const label = this.currentCropDimensionLabel;
      if (!label || !this.cropCanvasContext || !this.cropRect) return;

      const ctx = this.cropCanvasContext;
      const canvasWidth = this.cropCanvasSize.width;
      const canvasHeight = this.cropCanvasSize.height;
      const fontSize = 14;
      const lineHeight = 18;
      const gap = 8;
      const rightEdge = this.cropRect.x + this.cropRect.width;
      const topEdge = this.cropRect.y;
      const bottomEdge = this.cropRect.y + this.cropRect.height;

      ctx.save();
      ctx.font = `700 ${fontSize}px Arial, sans-serif`;
      ctx.fillStyle = '#fff';
      ctx.textBaseline = 'bottom';
      if ('textRendering' in ctx) {
        ctx.textRendering = 'geometricPrecision';
      }

      const textWidth = ctx.measureText(label).width;
      const canDrawAbove = topEdge - gap - lineHeight >= 0;
      const canDrawRight = rightEdge + gap + textWidth <= canvasWidth;
      const canDrawBelow = bottomEdge + gap + lineHeight <= canvasHeight;
      const canDrawLeft = this.cropRect.x - gap - textWidth >= 0;
      const alignTextPosition = (value) => Math.round(value);

      if (canDrawAbove) {
        ctx.textAlign = 'right';
        const x = alignTextPosition(clamp(rightEdge, textWidth, canvasWidth));
        const y = alignTextPosition(topEdge - gap);
        ctx.fillText(label, x, y);
      } else if (canDrawRight) {
        ctx.textAlign = 'left';
        const x = alignTextPosition(rightEdge + gap);
        const y = alignTextPosition(clamp(topEdge + lineHeight, lineHeight, canvasHeight));
        ctx.fillText(label, x, y);
      } else if (canDrawBelow) {
        ctx.textAlign = 'right';
        const x = alignTextPosition(clamp(rightEdge, textWidth, canvasWidth));
        const y = alignTextPosition(bottomEdge + gap + lineHeight);
        ctx.fillText(label, x, y);
      } else if (canDrawLeft) {
        ctx.textAlign = 'right';
        const x = alignTextPosition(this.cropRect.x - gap);
        const y = alignTextPosition(clamp(topEdge + lineHeight, lineHeight, canvasHeight));
        ctx.fillText(label, x, y);
      } else {
        ctx.textAlign = 'right';
        const x = alignTextPosition(clamp(rightEdge, textWidth, canvasWidth));
        const y = alignTextPosition(clamp(topEdge - gap, lineHeight, canvasHeight));
        ctx.fillText(label, x, y);
      }

      ctx.restore();
    }

    getCanvasPoint(event) {
      const rect = this.cropCanvas.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    }

    getActiveCropPointerEntries() {
      return Array.from(this.cropPointers.entries()).slice(0, 2);
    }

    getCropPinchSnapshot() {
      const entries = this.getActiveCropPointerEntries();
      if (entries.length < 2) return null;

      const pointA = entries[0][1];
      const pointB = entries[1][1];
      return {
        distance: Math.max(1, getDistance(pointA, pointB)),
        center: getMidpoint(pointA, pointB),
      };
    }

    cropRectContains(point) {
      return (
        this.cropRect &&
        point.x >= this.cropRect.x &&
        point.x <= this.cropRect.x + this.cropRect.width &&
        point.y >= this.cropRect.y &&
        point.y <= this.cropRect.y + this.cropRect.height
      );
    }

    resetCropGestureState() {
      if (this.cropCanvas && this.cropPointers) {
        this.cropPointers.forEach((_, pointerId) => {
          this.releaseCropPointer(pointerId);
        });
        this.cropCanvas.classList.remove('is-dragging');
      }

      if (this.cropPointers) {
        this.cropPointers.clear();
      }
      this.cropDrag = null;
      this.cropPinch = null;
    }

    captureCropPointer(pointerId) {
      if (!this.cropCanvas || !this.cropCanvas.setPointerCapture) return;

      try {
        this.cropCanvas.setPointerCapture(pointerId);
      } catch (error) {
        // Synthetic pointer events and some older touch browsers can reject capture.
      }
    }

    releaseCropPointer(pointerId) {
      if (!this.cropCanvas || !this.cropCanvas.hasPointerCapture || !this.cropCanvas.releasePointerCapture) return;

      try {
        if (this.cropCanvas.hasPointerCapture(pointerId)) {
          this.cropCanvas.releasePointerCapture(pointerId);
        }
      } catch (error) {
        // Pointer capture may already be gone after touch cancellation or modal close.
      }
    }

    startCropDrag(pointerId, point) {
      if (!this.cropRectContains(point)) return;

      this.cropDrag = {
        pointerId,
        offsetX: point.x - this.cropRect.x,
        offsetY: point.y - this.cropRect.y,
      };
      this.cropCanvas.classList.add('is-dragging');
    }

    stopCropDrag() {
      this.cropDrag = null;
      if (this.cropCanvas) {
        this.cropCanvas.classList.remove('is-dragging');
      }
    }

    startCropPinch() {
      const snapshot = this.getCropPinchSnapshot();
      if (!snapshot || !this.cropRect) return;

      this.stopCropDrag();
      this.cropPinch = {
        initialDistance: snapshot.distance,
        initialRect: { ...this.cropRect },
      };
    }

    updateCropPinch() {
      if (!this.cropPinch) return;

      const snapshot = this.getCropPinchSnapshot();
      if (!snapshot) return;

      const factor = snapshot.distance / this.cropPinch.initialDistance;
      this.scaleCropRect(factor, snapshot.center, this.cropPinch.initialRect);
    }

    resumeCropDragFromActivePointer() {
      if (this.cropPointers.size !== 1) return;

      const entry = Array.from(this.cropPointers.entries())[0];
      if (!entry) return;

      const [pointerId, point] = entry;
      this.startCropDrag(pointerId, point);
    }

    onCropPointerDown(event) {
      if (!this.cropModalOpen || !this.cropRect || !this.cropImageRect) return;

      const point = this.getCanvasPoint(event);
      event.preventDefault();
      this.cropPointers.set(event.pointerId, point);
      this.captureCropPointer(event.pointerId);

      if (this.cropPointers.size >= 2) {
        this.startCropPinch();
        return;
      }

      this.startCropDrag(event.pointerId, point);
    }

    onCropPointerMove(event) {
      if (!this.cropRect || !this.cropImageRect || !this.cropPointers.has(event.pointerId)) return;

      event.preventDefault();
      const point = this.getCanvasPoint(event);
      this.cropPointers.set(event.pointerId, point);

      if (this.cropPointers.size >= 2) {
        if (!this.cropPinch) {
          this.startCropPinch();
        }
        this.updateCropPinch();
        return;
      }

      if (!this.cropDrag || this.cropDrag.pointerId !== event.pointerId) return;

      this.cropRect = this.clampCropRect({
        x: point.x - this.cropDrag.offsetX,
        y: point.y - this.cropDrag.offsetY,
        width: this.cropRect.width,
        height: this.cropRect.height,
      });
      this.drawCropCanvas();
    }

    onCropPointerUp(event) {
      const wasPinching = Boolean(this.cropPinch);
      this.cropPointers.delete(event.pointerId);

      if (this.cropDrag && this.cropDrag.pointerId === event.pointerId) {
        this.stopCropDrag();
      }

      this.releaseCropPointer(event.pointerId);

      if (this.cropPointers.size >= 2) {
        this.startCropPinch();
      } else if (wasPinching) {
        this.cropPinch = null;
        this.stopCropDrag();
        this.resumeCropDragFromActivePointer();
      } else {
        this.cropPinch = null;
      }
    }

    onCropWheel(event) {
      if (!this.cropModalOpen || !this.cropRect || !this.cropImageRect) return;

      event.preventDefault();
      const factor = event.deltaY < 0 ? 1.08 : 0.92;
      const centerX = this.cropRect.x + this.cropRect.width / 2;
      const centerY = this.cropRect.y + this.cropRect.height / 2;
      this.scaleCropRect(factor, { x: centerX, y: centerY });
    }

    getCropSourceSku() {
      const image = this.getCropImageData(this.activeCropImageIndex);
      const skus = [...new Set((image?.sourceSkus || []).map((sku) => String(sku).trim()).filter((sku) => sku && !/^ZJ-KHDZ-/.test(sku)))];
      return skus.length === 1 ? skus[0] : (this.hasBackdropOrientation()&&!/^ZJ-KHDZ-/.test(this.currentVariant?.sku||'')?this.currentVariant?.sku:null);
    }

    updateCropSourceAvailability() {
      const valid = this.isCustomerUploadMode() || Boolean(this.getCropSourceSku() && (this.getCropImageData(this.activeCropImageIndex)?.imageId || (this.hasBackdropOrientation() && this.getCropImageData(this.activeCropImageIndex)?.id)));
      if (this.cropConfirmButton) this.cropConfirmButton.disabled = !valid || !this.cropImageElement;
      if (!valid) this.setCropModalMessage(this.getMessage('cropSourceUnavailable', 'This image cannot be matched to a unique source SKU. Please choose another image.'));
      return valid;
    }

    buildCropData() {
      const image = this.getCropImageData(this.activeCropImageIndex);
      const sku = this.getCropSourceSku();
      if (!sku || !(image?.imageId || (this.hasBackdropOrientation() && image?.id)) || !image?.productId || !this.cropRect || !this.cropImageRect) return null;
      const bounds = this.cropImageRect;
      const x = clamp((this.cropRect.x - bounds.x) / bounds.width, 0, 1);
      const y = clamp((this.cropRect.y - bounds.y) / bounds.height, 0, 1);
      const width = Math.min(this.cropRect.width / bounds.width, 1 - x);
      const height = Math.min(this.cropRect.height / bounds.height, 1 - y);
      if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;
      // Quantize only the confirmed snapshot; keep dragging at full precision.
      const xUnits = Math.round(x * 1000);
      const yUnits = Math.round(y * 1000);
      const widthUnits = Math.min(Math.round(width * 1000), 1000 - xUnits);
      const heightUnits = Math.min(Math.round(height * 1000), 1000 - yUnits);
      if (widthUnits <= 0 || heightUnits <= 0) return null;
      const rect = { x: xUnits / 1000, y: yUnits / 1000, width: widthUnits / 1000, height: heightUnits / 1000 };
      if (this.isMirrored()) rect.x = Math.max(0, 1 - rect.x - rect.width);
      return { rect, mirrored: this.isMirrored() };
    }

    setConfirmedCropData(data) {
      this.confirmedCropData = data;
      this.productForm.mirroredBackdrop?.onCropData(data);
      const field = this.query('[data-custom-size-crop-data]');
      if (field) { field.value = data ? JSON.stringify(data) : ''; field.disabled = !data; }
    }

    async confirmCrop() {
      if (!this.cropImageElement || !this.cropRect || !this.cropImageRect || !this.cropConfirmButton) return;

      const cropData = this.isCustomerUploadMode() ? null : this.buildCropData();
      if (!this.isCustomerUploadMode() && !cropData) {
        if (this.updateCropSourceAvailability()) {
          this.setCropModalMessage(this.getMessage('cropRegionTooSmall', 'The crop area is too small. Please select a larger area.'));
        }
        return;
      }
      const cropSourceImage = this.cropImageElement;
      const cropSelection = this.getCropSourceSelection();
      const cropAspectRatio = this.cropAspectRatio;
      const cropLoadId = this.cropImageLoadingId;
      const uploadItem = this.isCustomerUploadMode() ? this.uploadItems[this.activeCropImageIndex] : null;
      const isCurrentCrop = () => this.cropModalOpen && this.cropImageElement === cropSourceImage &&
        this.cropImageLoadingId === cropLoadId && this.cropAspectRatio === cropAspectRatio &&
        (!this.isCustomerUploadMode() || this.uploadItems[this.activeCropImageIndex] === uploadItem);

      this.cropConfirmButton.disabled = true;
      this.cropConfirmButton.setAttribute('aria-busy', 'true');

      try {
        const file = await this.exportCropFile(cropData);
        this.productionImageError=false;
        if (!isCurrentCrop()) return;

        if (this.isCustomerUploadMode()) {
          this.replaceCustomerUploadWithCrop(file, cropSelection);
          this.setWarning('');
          this.closeCropModal();
          return;
        }

        this.cropFile = file;
        this.setConfirmedCropData(cropData);
        this.setCropPreference(this.getMessage('customerCrop', 'Customer crop'));
        this.setCropFileInput(file);
        this.setCropPreviewFile(file);
        this.updateCropStatus();
        this.setWarning('');
        this.closeCropModal();
      } catch (error) {
        if (!isCurrentCrop()) return;
        this.clearCropSelection();
        this.productionImageError=!this.isCustomerUploadMode();
        const exportError = error.code === 'CROP_FILE_TOO_LARGE'
          ? this.getMessage('cropFileTooLarge', 'The cropped image exceeds 20 MB after processing. Please choose a smaller crop or cancel to keep your original image.')
          : this.getMessage('unableToExportCrop', 'Unable to export the crop. Our designers can adjust it for you.');
        this.setCropModalMessage(exportError);
        this.setWarning(exportError);
      } finally {
        this.updateCropSourceAvailability();
        this.cropConfirmButton.removeAttribute('aria-busy');
      }
    }

    async exportCropFile(cropData = null) {
      const scaleX = this.cropImageElement.naturalWidth / this.cropImageRect.width;
      const scaleY = this.cropImageElement.naturalHeight / this.cropImageRect.height;
      // Align to source pixels: fractional coordinates resample fine image detail.
      const sx = cropData ? Math.round(cropData.rect.x * this.cropImageElement.naturalWidth) : clamp(Math.round(this.getCropSourceSelection().x), 0, this.cropImageElement.naturalWidth - 1);
      const sy = cropData ? Math.round(cropData.rect.y * this.cropImageElement.naturalHeight) : clamp(Math.round((this.cropRect.y - this.cropImageRect.y) * scaleY), 0, this.cropImageElement.naturalHeight - 1);
      const sw = cropData ? Math.round((cropData.rect.x + cropData.rect.width) * this.cropImageElement.naturalWidth) - sx : clamp(Math.round(this.cropRect.width * scaleX), 1, this.cropImageElement.naturalWidth - sx);
      const sh = cropData ? Math.round((cropData.rect.y + cropData.rect.height) * this.cropImageElement.naturalHeight) - sy : clamp(Math.round(this.cropRect.height * scaleY), 1, this.cropImageElement.naturalHeight - sy);
      if (sw < 1 || sh < 1) throw new Error('Crop region is empty.');
      const outputCanvas = document.createElement('canvas');

      try {
        // Customer uploads keep the original file; this bounded image is only a reference.
        const previewScale = this.isCustomerUploadMode() ? Math.min(1, 1600 / Math.max(sw, sh)) : 1;
        outputCanvas.width = Math.max(1, Math.round(sw * previewScale));
        outputCanvas.height = Math.max(1, Math.round(sh * previewScale));
        const outputContext = outputCanvas.getContext('2d');
        if (!outputContext) throw new Error('Unable to create crop canvas.');
        if (this.isMirrored()) { outputContext.translate(outputCanvas.width, 0); outputContext.scale(-1, 1); }
        outputContext.drawImage(this.cropImageElement, sx, sy, sw, sh, 0, 0, outputCanvas.width, outputCanvas.height);

        const blob = await canvasToBlob(outputCanvas, cropExportType);
        if (blob.size >= maxCropExportBytes) {
          const error = new Error('Lossless crop exceeds the upload file limit.');
          error.code = 'CROP_FILE_TOO_LARGE';
          throw error;
        }
        const filePrefix = this.isCustomerUploadMode() ? `custom-upload-${this.activeCropImageIndex + 1}-crop` : 'custom-size-crop';

        return new File([blob], `${filePrefix}-${Date.now()}.png`, {
          type: cropExportType,
          lastModified: Date.now(),
        });
      } finally {
        // Release the temporary full-resolution bitmap, including on export failure.
        outputCanvas.width = 0;
        outputCanvas.height = 0;
      }
    }

    update() {
      this.currentVariant = this.findVariantBySelectedOptions() || this.findVariantById(this.getCurrentVariantId()) || this.currentVariant;

      if (this.isUploadOnlyMode()) {
        const fixedVariantDimension = this.syncFixedVariantDimension(true);

        this.root.hidden = false;
        this.root.setAttribute('data-custom-size-media-only', '');
        this.body.hidden = false;
        this.widthInput.required = false;
        this.heightInput.required = false;
        this.updateDynamicCheckoutVisibility(true);
        this.clearHiddenFields();
        this.updateSeamNotice(null);
        this.setMessage('');
        this.setWarning('');
        this.result.hidden = true;
        this.updateAddToCartButton(false);

        if (fixedVariantDimension) {
          const { widthRaw, heightRaw, unit } = fixedVariantDimension;
          this.cropAspectRatio = toNumber(widthRaw) / toNumber(heightRaw);
          this.setCropDimensionLabel(widthRaw, heightRaw, unit);
        } else {
          this.cropAspectRatio = null;
          this.clearCropDimensionLabel();
        }

        this.updateCropUi(Boolean(fixedVariantDimension));
        return;
      }

      const targetVariantSelected = this.isCustomVariantSelected();
      const customSizeVisible = targetVariantSelected;
      const fixedVariantDimension = this.syncFixedVariantDimension(customSizeVisible);
      const fixedVariantSelected = Boolean(fixedVariantDimension);
      const customSizePricingSelected = targetVariantSelected && !fixedVariantSelected;
      const dimensionsEditable = customSizePricingSelected;
      const materialKey = this.getMaterialKey();
      const materialRule = this.getMaterialRule(materialKey);

      this.root.hidden = this.hasBackdropOrientation() && !this.isCustomerUploadMode() ? !customSizePricingSelected : !customSizeVisible;
      this.root.toggleAttribute('data-custom-size-media-only', fixedVariantSelected);
      this.body.hidden = !customSizeVisible;
      this.widthInput.required = customSizePricingSelected;
      this.heightInput.required = customSizePricingSelected;
      this.setDimensionInputsState({
        editable: dimensionsEditable,
        widthRaw: fixedVariantDimension ? fixedVariantDimension.widthRaw : '',
        heightRaw: fixedVariantDimension ? fixedVariantDimension.heightRaw : '',
        unit: fixedVariantDimension ? fixedVariantDimension.unit : this.unitSelect.value || this.config.defaultUnit || 'ft',
      });
      this.updateDynamicCheckoutVisibility((this.pricingEnabled && this.hasTargetVariantSku()) || this.isMirrored() || Boolean(this.cropFile));

      if (this.materialOutput) {
        this.materialOutput.textContent =
          customSizePricingSelected && materialRule && materialRule.name ? materialRule.name : '';
      }

      if (!customSizeVisible) {
        this.updateSeamNotice(null);
        this.clearHiddenFields();
        this.clearCropSelection({ resetPreference: false });
        this.clearCustomerUploads();
        this.currentDimensionSignature = '';
        this.clearCropDimensionLabel();
        this.cropAspectRatio = null;
        this.updateCropUi(false);
        this.setMessage('');
        this.setWarning('');
        this.result.hidden = true;
        this.updateAddToCartButton(false);
        this.restoreVariantPriceDisplays();
        return;
      }

      const widthRaw = fixedVariantDimension ? fixedVariantDimension.widthRaw : this.widthInput.value.trim();
      const heightRaw = fixedVariantDimension ? fixedVariantDimension.heightRaw : this.heightInput.value.trim();
      const unit = fixedVariantDimension ? fixedVariantDimension.unit : this.unitSelect.value || this.config.defaultUnit || 'ft';
      const width = toNumber(widthRaw);
      const height = toNumber(heightRaw);
      const dimensions = width !== null && height !== null ? this.getNormalizedDimensions(width, height, unit) : null;

      this.clearHiddenFields();

      if (fixedVariantSelected) {
        const dimensionSignature = this.getDimensionSignature(widthRaw, heightRaw, unit);
        if (this.currentDimensionSignature && this.currentDimensionSignature !== dimensionSignature) {
          this.clearCropSelection();
        }
        this.currentDimensionSignature = dimensionSignature;
        this.cropAspectRatio = width / height;
        this.setCropDimensionLabel(widthRaw, heightRaw, unit);
        this.updateCropUi(true);
        this.updateSeamNotice(null);
        this.setMessage('');
        this.setWarning('');
        this.result.hidden = true;
        this.updateAddToCartButton(false);
        this.restoreVariantPriceDisplays();
        return;
      }

      const error = this.validate(widthRaw, heightRaw, unit, materialRule, dimensions);

      this.setMessage(error);
      this.result.hidden = Boolean(error);
      this.updateAddToCartButton(Boolean(error));

      if (error) {
        this.clearCropSelection();
        this.currentDimensionSignature = '';
        this.clearCropDimensionLabel();
        this.cropAspectRatio = null;
        this.updateCropUi(false);
        this.updateSeamNotice(null);
        this.setWarning('');
        this.restoreVariantPriceDisplays();
        return;
      }

      const dimensionSignature = this.getDimensionSignature(widthRaw, heightRaw, unit);
      if (this.currentDimensionSignature && this.currentDimensionSignature !== dimensionSignature) {
        this.clearCropSelection();
      }
      this.currentDimensionSignature = dimensionSignature;
      this.cropAspectRatio = width / height;
      this.setCropDimensionLabel(widthRaw, heightRaw, unit);
      this.updateCropUi(true);
      this.updateSeamNotice(this.shouldShowSeamNotice(materialKey, width, height, unit) ? materialKey : null);

      const price = this.calculatePrice(dimensions, materialRule);
      const cents = Math.round(price * 100);
      const formattedPrice = formatMoney(cents);

      this.priceOutput.innerHTML = formattedPrice;
      this.updatePriceDisplays(formattedPrice);
      this.updateHiddenFields(widthRaw, heightRaw, unit, materialRule, dimensions, price);
      this.setWarning('');
    }
  }

  const initCustomSizePricing = (root) => {
    if (!root || root.getAttribute(initializedAttribute) === 'true') return;

    root.setAttribute(initializedAttribute, 'true');
    root.customSizePricingController = new CustomSizePricingController(root);
    root.customSizePricingController.productForm.mirroredBackdrop?.refresh();
  };

  const initAll = () => {
    document.querySelectorAll('custom-size-pricing').forEach(initCustomSizePricing);
  };

  if (window.customElements && !window.customElements.get('custom-size-pricing')) {
    window.customElements.define(
      'custom-size-pricing',
      class extends HTMLElement {
        connectedCallback() {
          initCustomSizePricing(this);
        }
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
